<template>
  <OperationFormLayoutSkeleton
    v-if="loading"
    isConfigurationForm
  />
  <form @submit.prevent="save">
    <OperationFormLayout
      v-if="!loading && currentOrganisation"
      :id="$route.params.shipId"
      :formDisabled="disable"
      :formSaving="saving"
      isConfigurationForm
      operationType="ship"
      @cancel="back"
    >
      <!-- Details section -->
      <OperationFormSection
        :modifiers="[
          'no-margin',
          'full-max-width',
          'responsive',
          'default',
          'border-bottom',
        ]"
        :sectionDescription="$t('ships.details_description')"
        :sectionTitle="$t('ships.details_title')"
      >
        <FormRow
          :errors="nameErrors"
          :label="$t('operations.operation_name_label_ship')"
          labelFor="operation_name"
          :modifiers="['no-padding']"
        >
          <div class="form-row__input form-row__input--no-peek">
            <div :class="{ input: true, 'input--error': nameErrors }">
              <input
                id="operation_name"
                v-model="ship.name"
              >
            </div>
          </div>
        </FormRow>
        <FormRow
          :label="$t('ships.ship_division_label')"
          :modifiers="['no-padding']"
        >
          <div class="form-row__input form-row__input--no-peek">
            <DivisionSelect
              v-model="ship.division"
              :divisions="divisions"
            />
          </div>
        </FormRow>
        <FormRow
          v-if="ship.division && ship.division.isNew"
          :errors="divisionNameErrors"
          :label="$t('ships.ship_new_division_label')"
          labelFor="new_division_name"
          :modifiers="['no-padding']"
        >
          <div class="form-row__input form-row__input--no-peek">
            <div :class="{ input: true, 'input--error': divisionNameErrors }">
              <input
                id="new_division_name"
                v-model="ship.division.name"
              >
            </div>
          </div>
        </FormRow>
        <FormRow
          :label="$t('ships.ship_time_zone')"
          :modifiers="['no-padding']"
        >
          <div class="form-row__input form-row__input--no-peek">
            <Select
              v-model="ship.time_zone"
              :items="timeZones"
              :modifiers="['input', 'base', 'short-dropdown']"
              :placeholder="displayTimeZone"
            />
          </div>
        </FormRow>
      </OperationFormSection>

      <!-- Configure elements section -->
      <OperationFormSection
        :modifiers="[
          'no-margin',
          'full-max-width',
          'responsive',
          'default',
          'border-bottom',
        ]"
        :sectionDescription="$t('ships.diagram_help_text')"
        :sectionTitle="$t('ships.ship_diagram')"
      >
        <FormRow :modifiers="['no-padding']">
          <template v-if="diagramImage">
            <img
              class="ship-diagram"
              :src="diagramImage"
            >
            <FormStandAloneErrorMessage
              v-if="!imageValid"
              :errorMessage="imageValidationMessage"
            />
            <div
              :class="{
                'form-layout__row--mobile form-layout__row--inline': $route.params.shipId,
                'form-layout__row--margin-top': true,
              }"
            >
              <div
                :class="{ 'form-layout__column form-layout__column--inline-button': $route.params.shipId }"
              >
                <label
                  class="diagram-button button"
                  for="ship_change_diagram"
                >
                  {{ $t('ships.change_diagram') }}
                  <FileUploader
                    id="ship_change_diagram"
                    :acceptedFileTypes="acceptedFileTypes"
                    fileType="image"
                    :label="$t('issues.add_image')"
                    :modifiers="['hide']"
                    :multiple="false"
                    @update-blob="updateBlob"
                    @update-reader="updateReader"
                  />
                </label>
              </div>
              <div
                v-if="$route.params.shipId"
                class="form-layout__column form-layout__column--inline-button"
              >
                <router-link
                  class="button"
                  :to="{ name: 'shipManageRegions' }"
                >
                  <ManageRegionSvg class="button__icon button--margin-right" />
                  {{ $t('ships.manage_regions') }}
                </router-link>
              </div>
            </div>
          </template>
          <label
            v-else
            class="diagram-button button"
            for="diagram"
          >
            {{ $t('ships.upload_diagram') }}
            <FileUploader
              id="diagram"
              :acceptedFileTypes="acceptedFileTypes"
              fileType="image"
              :label="$t('issues.add_image')"
              :modifiers="['hide']"
              :multiple="false"
              @update-blob="updateBlob"
              @update-error="updateError"
              @update-reader="updateReader"
            />
          </label>
          <FormStandAloneErrorMessage
            v-if="!imageValid && !diagramImage"
            :errorMessage="imageValidationMessage"
          />
        </FormRow>
      </OperationFormSection>
    </OperationFormLayout>
  </form>
</template>

<script setup>
import { computed, inject, onMounted, reactive, ref, watch } from 'vue';

// Utils
import I18n from 'i18n-js';
import { acceptedFileTypes } from '@/config/accepted_file_types';
import { useRoute, useRouter } from 'vue-router';
import dig from '@/functions/dig';
import timeZones from '@/functions/time_zones';
import useAjax from '@/functions/use_ajax';
import useUploader from '@/vendor/use_uploader';
import isDesktop from '@/functions/responsive';

// Components
import DivisionSelect from '@/components/operations/division_select.vue';
import FormRow from '@/components/form/row.vue';
import OperationFormLayout from '@/components/operations/form/operation_form_layout.vue';
import OperationFormLayoutSkeleton from '@/components/operations/form/operation_form_layout_skeleton.vue';
import OperationFormSection from '@/components/operations/form/operation_form_section.vue';
import Select from '@/components/form/select.vue';
import FormStandAloneErrorMessage from '@/components/form/form_stand_alone_error_message.vue';
import FileUploader from '@/components/generic/file_uploader.vue';

// Svg
import ManageRegionSvg from '@/images/manage-region.svg';

const createNewShip = () => ({
  name: '',
  division: null,
  map_tile_overlays: [],
  time_zone: Intl.DateTimeFormat().resolvedOptions().timeZone,
});

const route = useRoute();
const router = useRouter();
const uploader = useUploader();
const ajax = useAjax();
const currentOrganisation = inject('currentOrganisation');
const loading = ref(false);
const initialShip = ref(null);
const ship = reactive(createNewShip());
const errors = ref(null);
const saving = ref(false);
const divisions = ref(null);
const diagramImage = ref(null);
const disable = ref(true);
let diagramUpload;

const imageValid = ref(true);
const imageValidationMessage = ref(null);
const warningText = ref(null);

// All endpoints that this component uses
const ENDPOINTS = {
  divisions: `/organisations/${route.params.organisationId}/divisions`,
  ships: `/organisations/${route.params.organisationId}/ships/`,
  ship: `/organisations/${route.params.organisationId}/ships/${route.params.shipId}`,
  get post_or_patch() {
    return route.params.shipId ? this.ship : this.ships;
  },
};

/**
 * Form validation, check if the form was updated,
 * if form is updated enable the save button
 */
watch(() => ship, (newValue) => {
    const formFields = [
      'name',
      'division',
      'division_id',
      'time_zone'
    ];

    const changedFields = formFields.filter((field) => {
      // Check if a division exists
      if (!Object.hasOwn(initialShip.value, field)) return;
      // Check if a division was set (if it was then a division object will exist)
      if ((typeof newValue[field] === 'object' && newValue[field] !== null) && !initialShip.value[field])
        return newValue[field]?.id !== initialShip.value['division_id'];
      // Compare division objects
      if (typeof newValue[field] === 'object')
        return (JSON.stringify(newValue[field]) !== JSON.stringify(initialShip.value[field]));
      // Compare other string instances
      return newValue[field] !== initialShip.value[field];
    });

    // Check for new division
    const hasNewDivision = !Object.hasOwn(initialShip.value, 'division') && Object.hasOwn(newValue, 'division') && newValue['division'] !== null;
    disable.value = changedFields.length <= 0 && !hasNewDivision;
  },
  { deep: true }
);

/**
 * Check if diagram was updated
 */
watch(() => diagramImage, (newValue) => {
    disable.value = initialShip.value.diagram_thumbnail_url === newValue.value;
  },
  { deep: true }
);

const loadDivisions = async () => {
  let { ok, data } = await ajax(ENDPOINTS.divisions);

  if (ok) {
    divisions.value = data.divisions;
  }
};

const loadShip = async () => {
  if (route.params.shipId) {
    loading.value = true;
    let { ok, data } = await ajax(ENDPOINTS.ship);

    if (ok) {
      initialShip.value = data;
      Object.assign(ship, data);
      diagramImage.value = data?.diagram_thumbnail_url;
      loading.value = false;
    }
  } else {
    initialShip.value = createNewShip();
  }
};

onMounted(() => {
  loadDivisions();
  loadShip();
});

const formatDivisionAttributes = () => {
  if (ship.division?.isNew) {
    return {
      division_id: 'new',
      new_division: ship.division.name,
    };
  } else {
    return {
      division_id: ship.division?.id || null,
    };
  }
};

const formatShipForPost = async () => {
  let shipParams = {
    name: ship.name,
    time_zone: ship.time_zone,
    ...formatDivisionAttributes(),
  };

  if (diagramUpload) {
    let blob = await uploader.upload(diagramUpload);
    shipParams.diagram = {
      source: blob.signed_id,
      source_filename: blob.filename,
    };
  }

  return { ship: shipParams };
};

const displayTimeZone = computed(() => {
  return ship.time_zone
    ? ship.time_zone
    : I18n.t('ships.no_ship_time_zone');
});

const save = async () => {
  errors.value = null;
  saving.value = true;

  let method = route.params.shipId ? 'PATCH' : 'POST';
  let { ok, data } = await ajax(ENDPOINTS.post_or_patch, {
    method,
    body: await formatShipForPost(),
  });

  if (ok) {
    router.replace({
      name: isDesktop() ? 'ship' : 'shipConfiguration',
      params: { shipId: data.id }
    });
  } else {
    errors.value = data;
  }

  saving.value = false;
};

const nameErrors = computed(() => dig(errors.value, 'ship', 'name'));
const divisionNameErrors = computed(() =>
  dig(errors.value, 'division', 'division', 'name')
);

function updateBlob(file) {
  if (!file?.upload) return;
  diagramUpload = file.upload;
}

function updateReader(file) {
  if (!file?.url) return;
  diagramImage.value = file.url;
  warningText.value = I18n.t('general.warning_text');
}

function updateError(file) {
  if (!file?.filename) return;

  if (!acceptedFileTypes.includes(file.filename)) {
    imageValidationMessage.value = I18n.t('general.image_validation_message', {
      filename: file.filename,
    });
    imageValid.value = false;
  } else {
    imageValid.value = true;
  }
}

function back() {
  router.replace({
    name: isDesktop() ? 'ship' : 'shipConfiguration',
    params: { shipId: route.params.shipId },
  });
}
</script>

<style scoped lang="scss">
@import '@/stylesheets/global';

.ship-diagram {
  width: 100%;
}
</style>
